import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, DollarSign, Clock, EuroIcon, Plus, Save, User, CheckCircle, ChevronDown, Check, X, Edit, CalendarDays, Trash2, Archive, FileSpreadsheet, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { projetsService, rattachementsService } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { useApp } from '../contexts/AppContext';
import { TYPES_FACTURATION, CATEGORIES_TEMPS, STATUTS_PROJET } from '../data/mockData';
import { getCurrentWeek, weekInputToDate, formatDateForAPI } from '../utils/dateUtils';

// Fonction helper pour calculer les valeurs de stabilisation
const calculateStabilisationValues = (projet) => {
  const stabilisationBudget = projet?.budget_stabilisation_lancement !== null && projet?.budget_stabilisation_lancement !== undefined
    ? projet.budget_stabilisation_lancement
    : Math.round((parseFloat(projet?.budget_build || 0) * 0.05) * 100) / 100;
  
  let stabilisationDebutDate = projet?.date_debut_stabilisation_lancement;
  let stabilisationFinDate = projet?.date_fin_stabilisation_lancement;
  
  // Calculer les dates si pas manuellement définies
  if (!stabilisationDebutDate && projet?.date_fin_build) {
    const debut = new Date(projet.date_fin_build);
    debut.setDate(debut.getDate() + 1);
    stabilisationDebutDate = debut.toISOString().split('T')[0];
  }
  
  if (!stabilisationFinDate && projet?.date_fin_build) {
    const fin = new Date(projet.date_fin_build);
    fin.setDate(fin.getDate() + 31);
    stabilisationFinDate = fin.toISOString().split('T')[0];
  }
  
  return {
    budget: stabilisationBudget,
    dateDebut: stabilisationDebutDate,
    dateFin: stabilisationFinDate
  };
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProjetDetail = () => {
  const { id } = useParams();
  const { state, actions } = useApp();

  const [projet, setProjet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemps, setLoadingTemps] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [showTempsModal, setShowTempsModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedPersonnes, setSelectedPersonnes] = useState([]);
  const [tempsDataByPersonne, setTempsDataByPersonne] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [existingTempsbyPersonne, setExistingTempsByPersonne] = useState({});
  
  // États pour la modification du statut
  const [isEditingStatut, setIsEditingStatut] = useState(false);
  const [statutEnEdition, setStatutEnEdition] = useState('');
  const [loadingStatut, setLoadingStatut] = useState(false);

  // États pour l'édition du projet
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nom: '',
    description: '',
    date_debut: '',
    date_fin: '',
    budget_analyse_design: '',
    budget_build: '',
    budget_test: '',
    budget_stabilisation_lancement: '',
    date_debut_analyse_design: '',
    date_fin_analyse_design: '',
    date_debut_build: '',
    date_fin_build: '',
    date_debut_test: '',
    date_fin_test: '',
    date_debut_stabilisation_lancement: '',
    date_fin_stabilisation_lancement: '',
    categorie: '',
    facturable: true,
    statut: ''
  });
  const [editErrors, setEditErrors] = useState({});
  const [savingProject, setSavingProject] = useState(false);

  // États pour la suppression/archivage du projet
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAction, setDeleteAction] = useState(''); // 'delete' ou 'archive'
  const [deletingProject, setDeletingProject] = useState(false);

  // États pour l'affectation de personnes à une phase
  const [showAffectationModal, setShowAffectationModal] = useState(false);
  const [selectedPhaseForAffectation, setSelectedPhaseForAffectation] = useState('build');
  const [selectedPersonnesForAffectation, setSelectedPersonnesForAffectation] = useState([]);
  const [selectedRoleProjet, setSelectedRoleProjet] = useState('developpeur');
  const [savingAffectation, setSavingAffectation] = useState(false);

  // États pour la suppression d'affectation
  const [showDeleteAffectationModal, setShowDeleteAffectationModal] = useState(false);
  const [affectationToDelete, setAffectationToDelete] = useState(null);
  const [deletingAffectation, setDeletingAffectation] = useState(false);


  // Charger le projet spécifique
  useEffect(() => {
    const chargerProjet = async () => {
      if (id) {
        setLoading(true);
        setError(null);
        try {
          const projetData = await projetsService.getById(id);
          setProjet(projetData.data);
        } catch (error) {
          console.error('Erreur lors du chargement du projet:', error);
          setError('Erreur lors du chargement du projet');
        } finally {
          setLoading(false);
        }
      }
    };

    chargerProjet();
  }, [id]);

  // Reset form when modal opens and load temps consommés
  useEffect(() => {
    if (showTempsModal) {
      setSelectedWeek(getCurrentWeek());
      setSelectedPersonnes([]);
      setTempsDataByPersonne({});
      setErrors({});
      setSaved(false);
      
      // Load temps consommés for the display
      if (!state.tempsConsommes?.data) {
        actions.loadAllTempsConsommes({ limit: 100 });
      }
    }
  }, [showTempsModal]);

  // Initialize temps data when personnes selection changes
  useEffect(() => {
    setTempsDataByPersonne(prev => {
      const newTempsData = { ...prev };
      
      // Add data for newly selected personnes
      selectedPersonnes.forEach(personneId => {
        if (!newTempsData[personneId]) {
          newTempsData[personneId] = {
            jours_consommes: '',
            facturable: projet?.facturable ?? true,
            commentaire: '',
            phase: 'build'
          };
        }
      });
      
      // Remove data for unselected personnes
      Object.keys(newTempsData).forEach(personneId => {
        if (!selectedPersonnes.includes(personneId)) {
          delete newTempsData[personneId];
        }
      });
      
      return newTempsData;
    });
  }, [selectedPersonnes, projet]);

  // Update existing temps when week or personnes selection changes in modal
  useEffect(() => {
    if (showTempsModal && selectedWeek && selectedPersonnes.length > 0) {
      const existingTemps = {};
      selectedPersonnes.forEach(personneId => {
        existingTemps[personneId] = getExistingTempsForPersonneWeek(personneId, selectedWeek);
      });
      setExistingTempsByPersonne(existingTemps);
    } else {
      setExistingTempsByPersonne({});
    }
  }, [selectedWeek, selectedPersonnes, state.tempsConsommes, showTempsModal]);

  const handleTempsChange = (personneId, fieldName, value) => {
    setTempsDataByPersonne(prev => ({
      ...prev,
      [personneId]: {
        ...prev[personneId],
        [fieldName]: value
      }
    }));
    
    // Clear related errors
    const errorKey = `${personneId}_${fieldName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
    setSaved(false);
  };

  const togglePersonne = (personneId) => {
    setSelectedPersonnes(prev => {
      const isSelected = prev.includes(personneId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== personneId)
        : [...prev, personneId];
      return newSelection;
    });
    setErrors(prev => ({ ...prev, personnes: '' }));
  };

  const toggleSelectAll = () => {
    const activePersonnes = state.personnes?.data?.filter(p => p.actif) || [];
    if (selectedPersonnes.length === activePersonnes.length) {
      setSelectedPersonnes([]);
    } else {
      setSelectedPersonnes(activePersonnes.map(p => p.id.toString()));
    }
    setErrors(prev => ({ ...prev, personnes: '' }));
  };

  // Function to get existing temps for a person and week in modal
  const getExistingTempsForPersonneWeek = (personneId, week) => {
    if (!state.tempsConsommes?.data || !week) return [];
    
    const weekDate = weekInputToDate(week);
    const apiWeekFormat = formatDateForAPI(weekDate);
    
    return state.tempsConsommes.data.filter(temps => 
      temps.id_personne === parseInt(personneId) && 
      temps.semaine === apiWeekFormat
    );
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (selectedPersonnes.length === 0) {
      newErrors.personnes = 'Sélectionnez au moins une personne';
    }
    
    if (!selectedWeek) {
      newErrors.semaine = 'Sélectionnez une semaine';
    }
    
    // Validate each personne's data
    selectedPersonnes.forEach(personneId => {
      const tempsData = tempsDataByPersonne[personneId];
      if (!tempsData) return;
      
      const joursConsommes = parseFloat(tempsData.jours_consommes);
      if (!tempsData.jours_consommes || joursConsommes <= 0) {
        newErrors[`${personneId}_jours_consommes`] = 'Les jours consommés doivent être supérieurs à 0';
      } else if (joursConsommes > 5) {
        newErrors[`${personneId}_jours_consommes`] = 'Les jours consommés ne peuvent pas dépasser 5 par semaine';
      }
      
      if (!tempsData.commentaire.trim()) {
        newErrors[`${personneId}_commentaire`] = 'Le commentaire est requis';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveTemps = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Convert week format to Monday date for API
      const weekDate = weekInputToDate(selectedWeek);
      const apiWeekFormat = formatDateForAPI(weekDate);
      
      // Save temps consommés for each selected personne
      const savePromises = selectedPersonnes.map(personneId => {
        const tempsData = tempsDataByPersonne[personneId];
        return actions.saveTempsConsomme({
          id_projet: parseInt(id),
          id_personne: parseInt(personneId),
          semaine: apiWeekFormat,
          jours_consommes: parseFloat(tempsData.jours_consommes),
          facturable: tempsData.facturable,
          commentaire: tempsData.commentaire,
          phase: tempsData.phase
        });
      });

      await Promise.all(savePromises);

      // Reload project data to update the display
      const projetData = await projetsService.getById(id);
      setProjet(projetData.data);

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setShowTempsModal(false);
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Close dropdown when clicking outside (modal version)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTempsModal && !event.target.closest('.relative')) {
        setIsDropdownOpen(false);
      }
    };

    if (showTempsModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTempsModal]);




  // Grouper les temps consommés par personne
  const tempsParPersonne = projet?.temps_consommes?.reduce((acc, temps) => {
    const personneId = temps.id_personne;
    if (!acc[personneId]) {
      acc[personneId] = {
        personne: temps.personne,
        temps: [],
        totalJours: 0,
        totalFacturable: 0,
        caEstime: 0,
        tempsParPhase: {}
      };
    }
    acc[personneId].temps.push(temps);
    acc[personneId].totalJours += parseFloat(temps.jours_consommes);
    
    // Calculer les temps par phase
    const phase = temps.phase || 'non_specifie';
    if (!acc[personneId].tempsParPhase[phase]) {
      acc[personneId].tempsParPhase[phase] = 0;
    }
    acc[personneId].tempsParPhase[phase] += parseFloat(temps.jours_consommes);
    
    if (temps.facturable) {
      acc[personneId].totalFacturable += parseFloat(temps.jours_consommes);
      acc[personneId].caEstime += parseFloat(temps.jours_consommes) * parseFloat(temps.personne.taux_journalier);
    }
    return acc;
  }, {}) || {};

  // Calculs pour les statistiques du projet
  const totalTempsConsomme = projet?.temps_consommes?.reduce((total, temps) => {
    return total + parseFloat(temps.jours_consommes);
  }, 0) || 0;

  const budgetTotal = parseFloat(projet?.budget_total || 0);
  const budgetRestant = budgetTotal - totalTempsConsomme;
  const pourcentageConsommation = budgetTotal > 0 ? (totalTempsConsomme / budgetTotal) * 100 : 0;

  // Fonction pour mettre à jour le statut du projet
  const handleStatutChange = async (nouveauStatut) => {
    if (!projet || nouveauStatut === projet.statut) {
      setIsEditingStatut(false);
      return;
    }

    setLoadingStatut(true);
    try {
      const projetMisAJour = await projetsService.update(projet.id, {
        ...projet,
        statut: nouveauStatut
      });
      
      // Mettre à jour le projet local
      setProjet(prev => ({ ...prev, statut: nouveauStatut }));
      
      // Recharger les données dans le contexte global si nécessaire
      if (actions.loadAllProjets) {
        actions.loadAllProjets();
      }
      
      setIsEditingStatut(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      setError('Erreur lors de la mise à jour du statut du projet');
    } finally {
      setLoadingStatut(false);
    }
  };

  // Fonction pour annuler l'édition du statut
  const handleCancelStatutEdit = () => {
    setIsEditingStatut(false);
    setStatutEnEdition('');
  };

  // Fonction pour commencer l'édition du statut
  const handleStartStatutEdit = () => {
    setStatutEnEdition(projet?.statut || '');
    setIsEditingStatut(true);
  };

  // Fonction pour ouvrir le modal d'édition du projet
  const handleStartProjectEdit = () => {
    if (projet) {
      const stabilisationValues = calculateStabilisationValues(projet);
      setEditFormData({
        nom: projet.nom || '',
        description: projet.description || '',
        date_debut: projet.date_debut || '',
        date_fin: projet.date_fin || '',
        budget_analyse_design: projet.budget_analyse_design || '',
        budget_build: projet.budget_build || '',
        budget_test: projet.budget_test || '',
        budget_stabilisation_lancement: stabilisationValues.budget,
        date_debut_analyse_design: projet.date_debut_analyse_design || '',
        date_fin_analyse_design: projet.date_fin_analyse_design || '',
        date_debut_build: projet.date_debut_build || '',
        date_fin_build: projet.date_fin_build || '',
        date_debut_test: projet.date_debut_test || '',
        date_fin_test: projet.date_fin_test || '',
        date_debut_stabilisation_lancement: stabilisationValues.dateDebut || '',
        date_fin_stabilisation_lancement: stabilisationValues.dateFin || '',
        categorie: projet.categorie || '',
        facturable: projet.facturable ?? true,
        statut: projet.statut || ''
      });
      setEditErrors({});
      setShowEditModal(true);
    }
  };

  // Fonction pour gérer les changements dans le formulaire d'édition
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    
    setEditFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // Recalculer le budget stabilisation si build change et qu'on n'a pas modifié manuellement
      if (name === 'budget_build' && !editFormData.budget_stabilisation_lancement) {
        const buildBudget = parseFloat(newValue || 0);
        updated.budget_stabilisation_lancement = Math.round((buildBudget * 0.05) * 100) / 100;
      }
      
      // Recalculer les dates stabilisation si date_fin_build change
      if (name === 'date_fin_build' && updated.date_fin_build) {
        if (!editFormData.date_debut_stabilisation_lancement) {
          const debut = new Date(updated.date_fin_build);
          debut.setDate(debut.getDate() + 1);
          updated.date_debut_stabilisation_lancement = debut.toISOString().split('T')[0];
        }
        if (!editFormData.date_fin_stabilisation_lancement) {
          const fin = new Date(updated.date_fin_build);
          fin.setDate(fin.getDate() + 31);
          updated.date_fin_stabilisation_lancement = fin.toISOString().split('T')[0];
        }
      }
      
      return updated;
    });
    
    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Fonction pour valider le formulaire d'édition
  const validateEditForm = () => {
    const newErrors = {};
    
    if (!editFormData.nom.trim()) {
      newErrors.nom = 'Le nom du projet est requis';
    }
    
    if (!editFormData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (!editFormData.date_debut) {
      newErrors.date_debut = 'La date de début est requise';
    }
    
    if (!editFormData.date_fin) {
      newErrors.date_fin = 'La date de fin est requise';
    }
    
    if (editFormData.date_debut && editFormData.date_fin && editFormData.date_debut >= editFormData.date_fin) {
      newErrors.date_fin = 'La date de fin doit être postérieure à la date de début';
    }

    // Validation des budgets
    const analyseDesign = parseFloat(editFormData.budget_analyse_design) || 0;
    const build = parseFloat(editFormData.budget_build) || 0;
    const test = parseFloat(editFormData.budget_test) || 0;
    
    if (analyseDesign < 0) {
      newErrors.budget_analyse_design = 'Le budget ne peut pas être négatif';
    }
    
    if (build < 0) {
      newErrors.budget_build = 'Le budget ne peut pas être négatif';
    }
    
    if (test < 0) {
      newErrors.budget_test = 'Le budget ne peut pas être négatif';
    }

    const stabilisation = parseFloat(editFormData.budget_stabilisation_lancement) || 0;
    
    if (stabilisation < 0) {
      newErrors.budget_stabilisation_lancement = 'Le budget ne peut pas être négatif';
    }

    if (analyseDesign + build + test + stabilisation <= 0) {
      newErrors.budget_total = 'Le total des budgets doit être supérieur à 0';
    }
    
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour sauvegarder les modifications du projet
  const handleSaveProject = async () => {
    if (!validateEditForm()) {
      return;
    }

    setSavingProject(true);
    try {
      const updatedProject = await projetsService.update(projet.id, {
        ...editFormData,
        budget_analyse_design: parseFloat(editFormData.budget_analyse_design) || 0,
        budget_build: parseFloat(editFormData.budget_build) || 0,
        budget_test: parseFloat(editFormData.budget_test) || 0,
        budget_stabilisation_lancement: parseFloat(editFormData.budget_stabilisation_lancement) || 20
      });
      
      // Recharger les données du projet
      const projetData = await projetsService.getById(id);
      setProjet(projetData.data);
      
      setShowEditModal(false);
      
      // Recharger les données dans le contexte global si nécessaire
      if (actions.loadAllProjets) {
        actions.loadAllProjets();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du projet:', error);
      setEditErrors({ general: 'Erreur lors de la mise à jour du projet' });
    } finally {
      setSavingProject(false);
    }
  };

  // Fonction pour déterminer si le projet peut être supprimé ou doit être archivé
  const canDeleteProject = () => {
    // Vérifier s'il y a du temps consommé
    const hasTempsConsomme = projet?.temps_consommes && projet.temps_consommes.length > 0;
    return !hasTempsConsomme;
  };

  // Fonction pour ouvrir le modal de confirmation de suppression/archivage
  const handleStartDelete = () => {
    const canDelete = canDeleteProject();
    setDeleteAction(canDelete ? 'delete' : 'archive');
    setShowDeleteModal(true);
  };

  // Fonction pour supprimer ou archiver le projet
  const handleDeleteProject = async () => {
    if (!projet) return;

    setDeletingProject(true);
    try {
      const response = await projetsService.delete(projet.id);
      
      // Rediriger vers la liste des projets après suppression/archivage
      window.location.href = '/projets';
      
    } catch (error) {
      console.error('Erreur lors de la suppression/archivage du projet:', error);
      setError('Erreur lors de la suppression/archivage du projet');
    } finally {
      setDeletingProject(false);
      setShowDeleteModal(false);
    }
  };

  // Fonction pour sauvegarder l'affectation de personnes à une phase
  const handleSaveAffectation = async () => {
    if (!projet || selectedPersonnesForAffectation.length === 0) {
      return;
    }

    setSavingAffectation(true);
    try {
      // Créer les rattachements pour chaque personne sélectionnée
      const affectationPromises = selectedPersonnesForAffectation.map(personneId => {
        return rattachementsService.create({
          id_projet: projet.id,
          id_personne: parseInt(personneId),
          phase: selectedPhaseForAffectation,
          role_projet: selectedRoleProjet,
          date_debut: projet.date_debut,
          date_fin: projet.date_fin,
          actif: true
        });
      });

      await Promise.all(affectationPromises);

      // Recharger les données du projet pour mettre à jour l'affichage
      const projetData = await projetsService.getById(projet.id);
      setProjet(projetData.data);

      // Fermer la modal et réinitialiser les states
      setShowAffectationModal(false);
      setSelectedPersonnesForAffectation([]);
      setSelectedPhaseForAffectation('build');
      setSelectedRoleProjet('developpeur');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'affectation:', error);
      setError('Erreur lors de l\'affectation de la personne à la phase');
    } finally {
      setSavingAffectation(false);
    }
  };

  // Fonction pour supprimer une affectation
  const handleDeleteAffectation = async () => {
    if (!affectationToDelete) {
      return;
    }

    setDeletingAffectation(true);
    try {
      await rattachementsService.delete(affectationToDelete.id);

      // Recharger les données du projet pour mettre à jour l'affichage
      const projetData = await projetsService.getById(projet.id);
      setProjet(projetData.data);

      // Fermer la modal et réinitialiser
      setShowDeleteAffectationModal(false);
      setAffectationToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'affectation:', error);
      setError('Erreur lors de la suppression de l\'affectation');
    } finally {
      setDeletingAffectation(false);
    }
  };

  // Fonction pour générer des couleurs différentes pour chaque personne
  const generateColors = (count) => {
    const colors = [
      'rgb(59, 130, 246)', // blue
      'rgb(16, 185, 129)', // green
      'rgb(245, 101, 101)', // red
      'rgb(251, 191, 36)', // yellow
      'rgb(139, 92, 246)', // purple
      'rgb(236, 72, 153)', // pink
      'rgb(6, 182, 212)', // cyan
      'rgb(251, 146, 60)', // orange
      'rgb(34, 197, 94)', // emerald
      'rgb(168, 85, 247)', // violet
    ];
    
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  };

  // Fonction pour extraire les personnes affectées à une phase
  const getPersonnesForPhase = (phaseKey) => {
    if (!projet?.rattachements_projet) return [];
    
    const personnesMap = {};
    projet.rattachements_projet.forEach(rattachement => {
      if (rattachement.phase === phaseKey && rattachement.actif) {
        const personneId = rattachement.id_personne;
        if (!personnesMap[personneId]) {
          personnesMap[personneId] = {
            id: personneId,
            prenom: rattachement.personne.prenom,
            nom: rattachement.personne.nom,
            role_projet: rattachement.role_projet,
            totalJours: 0
          };
        }
        personnesMap[personneId].totalJours += parseFloat(rattachement.jours_consommes || 0);
      }
    });
    
    return Object.values(personnesMap).sort((a, b) => b.totalJours - a.totalJours);
  };

  // Fonction pour extraire toutes les personnes affectées au projet
  const getAllPersonnesForProject = () => {
    if (!projet?.rattachements_projet) return [];
    
    const personnesMap = {};
    projet.rattachements_projet.forEach(rattachement => {
      if (rattachement.actif) {
        const personneId = rattachement.id_personne;
        if (!personnesMap[personneId]) {
          personnesMap[personneId] = {
            id: personneId,
            prenom: rattachement.personne.prenom,
            nom: rattachement.personne.nom,
            role: rattachement.personne.role,
            taux_journalier: parseFloat(rattachement.personne.taux_journalier),
            phasesAffectees: []
          };
        }
        // Ajouter la phase et les dates si la personne n'a pas déjà cette phase
        if (!personnesMap[personneId].phasesAffectees.find(p => p.phase === rattachement.phase)) {
          personnesMap[personneId].phasesAffectees.push({
            phase: rattachement.phase,
            role_projet: rattachement.role_projet,
            date_debut: rattachement.date_debut,
            date_fin: rattachement.date_fin
          });
        }
      }
    });
    
    return Object.values(personnesMap).sort((a, b) => 
      `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`)
    );
  };

  // Composant Gantt Chart des phases
  const GanttChart = () => {
    if (!projet || !projet.date_debut || !projet.date_fin) {
      return (
        <div className="text-center py-8 text-gray-500">
          <CalendarDays className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucune planification disponible</p>
        </div>
      );
    }

    const phases = [
      {
        name: 'Analyse & Design',
        key: 'analyse_design',
        dateDebut: projet.date_debut_analyse_design,
        dateFin: projet.date_fin_analyse_design,
        budget: parseFloat(projet.budget_analyse_design || 0),
        color: 'bg-blue-500',
        lightColor: 'bg-blue-100'
      },
      {
        name: 'Développement',
        key: 'build',
        dateDebut: projet.date_debut_build,
        dateFin: projet.date_fin_build,
        budget: parseFloat(projet.budget_build || 0),
        color: 'bg-green-500',
        lightColor: 'bg-green-100'
      },
      {
        name: 'Tests',
        key: 'test',
        dateDebut: projet.date_debut_test,
        dateFin: projet.date_fin_test,
        budget: parseFloat(projet.budget_test || 0),
        color: 'bg-orange-500',
        lightColor: 'bg-orange-100'
      },
      (() => {
        const stabValues = calculateStabilisationValues(projet);
        return {
          name: 'Stabilisation & Lancement',
          key: 'stabilisation_lancement',
          dateDebut: stabValues.dateDebut,
          dateFin: stabValues.dateFin,
          budget: stabValues.budget,
          color: 'bg-purple-500',
          lightColor: 'bg-purple-100'
        };
      })()
    ];

    // Calculer la durée totale du projet en jours
    const projetDebut = new Date(projet.date_debut);
    const projetFin = new Date(projet.date_fin);
    const dureeProjetMs = projetFin.getTime() - projetDebut.getTime();
    const dureeProjetJours = Math.ceil(dureeProjetMs / (1000 * 60 * 60 * 24));

    // Fonction pour calculer la position et largeur d'une phase
    const calculatePhasePosition = (dateDebut, dateFin) => {
      if (!dateDebut || !dateFin) return { left: 0, width: 0 };
      
      const phaseDebut = new Date(dateDebut);
      const phaseFin = new Date(dateFin);
      
      // Position par rapport au début du projet (en pourcentage)
      const offsetMs = phaseDebut.getTime() - projetDebut.getTime();
      const offsetJours = Math.max(0, offsetMs / (1000 * 60 * 60 * 24));
      const left = (offsetJours / dureeProjetJours) * 100;
      
      // Largeur de la phase (en pourcentage)
      const durееPhaseMs = phaseFin.getTime() - phaseDebut.getTime();
      const dureePhaseJours = Math.max(1, durееPhaseMs / (1000 * 60 * 60 * 24));
      const width = (dureePhaseJours / dureeProjetJours) * 100;
      
      return { left: Math.min(left, 100), width: Math.min(width, 100 - left) };
    };

    return (
      <div className="space-y-4">
        {/* En-tête avec dates du projet */}
        <div className="flex items-center justify-between text-sm text-gray-600 border-b pb-2">
          <span>Début: {formatDate(projet.date_debut)}</span>
          <span className="font-medium">{dureeProjetJours} jours</span>
          <span>Fin: {formatDate(projet.date_fin)}</span>
        </div>

        {/* Gantt des phases */}
        <div className="space-y-3">
          {phases.map((phase, index) => {
            const hasData = phase.dateDebut && phase.dateFin && phase.budget > 0;
            const position = hasData ? calculatePhasePosition(phase.dateDebut, phase.dateFin) : { left: 0, width: 0 };
            const personnesAffectees = getPersonnesForPhase(phase.key);
            
            return (
              <div key={index} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">{phase.name}</span>
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    {hasData ? (
                      <>
                        <span>{formatDate(phase.dateDebut)} - {formatDate(phase.dateFin)}</span>
                        <span className="font-medium">{phase.budget}j</span>
                      </>
                    ) : (
                      <span className="italic">Non planifié</span>
                    )}
                  </div>
                </div>
                
                {/* Barre de progression */}
                <div className="relative h-6 bg-gray-100 rounded-md overflow-hidden">
                  {hasData && (
                    <>
                      {/* Barre de la phase */}
                      <div
                        className={`absolute top-0 h-full ${phase.color} opacity-80 rounded-md flex items-center justify-center`}
                        style={{
                          left: `${position.left}%`,
                          width: `${position.width}%`
                        }}
                      >
                        {position.width > 15 && (
                          <span className="text-white text-xs font-medium">
                            {phase.budget}j
                          </span>
                        )}
                      </div>
                      
                      {/* Indicateur de temps consommé si disponible */}
                      {(() => {
                        const phaseData = projet.temps_consommes_par_phase?.[phase.key];
                        if (!phaseData || !phase.budget) return null;
                        
                        const consommePourcentage = (phaseData.total_jours / phase.budget) * position.width;
                        
                        return (
                          <div
                            className={`absolute top-0 h-full ${phase.lightColor} border-l-2 border-gray-400`}
                            style={{
                              left: `${position.left}%`,
                              width: `${Math.min(consommePourcentage, position.width)}%`
                            }}
                          />
                        );
                      })()}
                    </>
                  )}
                  
                  {!hasData && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs text-gray-400 italic">Non planifié</span>
                    </div>
                  )}
                </div>

                {/* Personnes affectées à cette phase */}
                {personnesAffectees.length > 0 && (
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-gray-600 font-medium">Personnes :</span>
                    <div className="flex items-center space-x-1">
                      {personnesAffectees.map((personne) => (
                        <div
                          key={personne.id}
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 ${phase.color}`}
                          title={`${personne.prenom} ${personne.nom} (${personne.totalJours.toFixed(1)}j)`}
                        >
                          {personne.prenom[0]}{personne.nom[0]}
                        </div>
                      ))}
                    </div>
                    <span className="text-gray-500 ml-2">
                      ({personnesAffectees.reduce((total, p) => total + p.totalJours, 0).toFixed(1)}j total)
                    </span>
                  </div>
                )}
                {personnesAffectees.length === 0 && hasData && (
                  <div className="text-xs text-gray-400 italic">
                    Aucune personne affectée à cette phase
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Légende */}
        <div className="flex items-center justify-center space-x-6 text-xs text-gray-500 pt-4 border-t mt-4 flex-wrap gap-4 justify-center">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
            <span>Analyse & Design</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
            <span>Développement</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded mr-1"></div>
            <span>Tests</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded mr-1"></div>
            <span>Stabilisation & Lancement</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-100 border-l-2 border-gray-400 rounded mr-1"></div>
            <span>Consommé</span>
          </div>
        </div>
      </div>
    );
  };

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!projet?.temps_consommes || projet.temps_consommes.length === 0) {
      return null;
    }

    // Obtenir toutes les semaines uniques triées à partir des temps consommés
    // Les semaines sont des dates du lundi de la semaine
    const allWeeks = [...new Set(projet.temps_consommes.map(t => t.semaine))]
      .sort((a, b) => new Date(a) - new Date(b));

    // Obtenir toutes les personnes uniques
    const personnes = [...new Set(projet.temps_consommes.map(t => t.id_personne))]
      .map(id => projet.temps_consommes.find(t => t.id_personne === id).personne);

    const colors = generateColors(personnes.length);

    // Créer les datasets pour chaque personne
    const datasets = personnes.map((personne, index) => {
      // Créer un map des temps par semaine pour cette personne
      const tempsByWeek = {};
      projet.temps_consommes
        .filter(t => t.id_personne === personne.id)
        .forEach(temps => {
          const week = temps.semaine; // Utiliser la date directement
          if (!tempsByWeek[week]) {
            tempsByWeek[week] = 0;
          }
          tempsByWeek[week] += parseFloat(temps.jours_consommes);
        });

      // Calculer les temps cumulés pour toutes les semaines
      let cumulativeTime = 0;
      const data = allWeeks.map(week => {
        // Ajouter le temps de cette semaine si il existe
        if (tempsByWeek[week]) {
          cumulativeTime += tempsByWeek[week];
        }
        // Sinon, garder le temps cumulé précédent
        return cumulativeTime;
      });

      return {
        label: `${personne.prenom} ${personne.nom}`,
        data: data,
        borderColor: colors[index],
        backgroundColor: colors[index] + '20', // Transparence de 20%
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
      };
    });

    // Créer des labels lisibles pour les semaines
    const formatWeekLabel = (dateString) => {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `Sem. du ${day}/${month}`;
    };

    return {
      labels: allWeeks.map(week => formatWeekLabel(week)),
      datasets: datasets,
    };
  };

  const chartData = prepareChartData();
  console.log(chartData);
  console.log('Données temps_consommes:', projet?.temps_consommes);
  console.log('Exemple de semaine:', projet?.temps_consommes?.[0]?.semaine);

  // Préparer les données pour le graphique par phase
  const preparePhaseChartData = () => {
    if (!projet?.temps_consommes_par_phase) {
      return null;
    }

    const phases = Object.keys(projet.temps_consommes_par_phase);
    const labels = phases.map(phase => {
      const categorie = CATEGORIES_TEMPS.find(cat => cat.value === phase);
      return categorie ? categorie.label : phase;
    });

    // Données de temps consommé
    const tempsConsommeData = phases.map(phase => 
      projet.temps_consommes_par_phase[phase].total_jours
    );

    // Données de budget planifié par phase
    const budgetParPhase = phases.map(phase => {
      switch(phase) {
        case 'analyse_design':
          return parseFloat(projet.budget_analyse_design || 0);
        case 'build':
          return parseFloat(projet.budget_build || 0);
        case 'test':
          return parseFloat(projet.budget_test || 0);
        default:
          return 0;
      }
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Budget planifié',
          data: budgetParPhase,
          backgroundColor: 'rgba(59, 130, 246, 0.5)', // bleu clair
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 2,
        },
        {
          label: 'Temps consommé',
          data: tempsConsommeData,
          backgroundColor: 'rgba(16, 185, 129, 0.5)', // vert clair
          borderColor: 'rgb(16, 185, 129)',
          borderWidth: 2,
        }
      ],
    };
  };

  const phaseChartData = preparePhaseChartData();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Évolution du temps consommé cumulé par personne',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} jours`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Semaines'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Temps cumulé (jours)'
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
  };

  const phaseChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Temps consommé par phase vs Budget planifié',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} jours`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Phases'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Temps (jours)'
        },
        beginAtZero: true,
      },
    },
  };

  // Résumé des jours par personne et par phase
  const resumeParPersonne = Object.values(tempsParPersonne).map(data => ({
    nom: `${data.personne.prenom} ${data.personne.nom}`,
    totalJours: data.totalJours,
  }));

  const resumeParPhase = projet?.temps_consommes?.reduce((acc, temps) => {
    const phase = temps.phase || 'non_specifie';
    const categorie = CATEGORIES_TEMPS.find(cat => cat.value === phase);
    const label = categorie ? categorie.label : (phase === 'non_specifie' ? 'Non spécifié' : phase);
    if (!acc[label]) acc[label] = 0;
    acc[label] += parseFloat(temps.jours_consommes);
    return acc;
  }, {}) || {};

  // Export Excel
  const handleExportExcel = () => {
    if (!projet?.temps_consommes || projet.temps_consommes.length === 0) return;

    const wb = XLSX.utils.book_new();

    const headerRows = [
      ['Projet', projet.nom],
      ['Description', projet.description || ''],
      [],
    ];

    const dataRows = projet.temps_consommes.map(temps => ({
      'Personne': `${temps.personne.prenom} ${temps.personne.nom}`,
      'Semaine': temps.semaine,
      'Jours': parseFloat(temps.jours_consommes),
      'Phase': CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase || '-',
      'Commentaire': temps.commentaire || '',
    }));

    const ws = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(ws, dataRows, { origin: 'A4' });

    const colWidths = [
      { wch: 30 }, // Personne
      { wch: 14 }, // Semaine
      { wch: 8 },  // Jours
      { wch: 20 }, // Phase
      { wch: 50 }, // Commentaire
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Temps');
    XLSX.writeFile(wb, `temps_${projet.nom.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
  };

  // Export PDF
  const handleExportPDF = () => {
    if (!projet?.temps_consommes || projet.temps_consommes.length === 0) return;

    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text(projet.nom, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    const descriptionLines = doc.splitTextToSize(projet.description || '', 180);
    doc.text(descriptionLines, 14, 28);

    let startY = 28 + descriptionLines.length * 5 + 6;

    // Résumé par personne
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('Résumé par personne', 14, startY);
    startY += 2;

    doc.autoTable({
      startY,
      head: [['Personne', 'Jours']],
      body: [
        ...resumeParPersonne.map(r => [r.nom, r.totalJours.toFixed(1)]),
        [{ content: 'Total', styles: { fontStyle: 'bold' } }, { content: totalTempsConsomme.toFixed(1), styles: { fontStyle: 'bold' } }],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 9 },
      margin: { left: 14 },
    });

    startY = doc.lastAutoTable.finalY + 8;

    // Résumé par phase
    doc.setFontSize(12);
    doc.text('Résumé par phase', 14, startY);
    startY += 2;

    doc.autoTable({
      startY,
      head: [['Phase', 'Jours']],
      body: [
        ...Object.entries(resumeParPhase).map(([phase, jours]) => [phase, jours.toFixed(1)]),
        [{ content: 'Total', styles: { fontStyle: 'bold' } }, { content: totalTempsConsomme.toFixed(1), styles: { fontStyle: 'bold' } }],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 9 },
      margin: { left: 14 },
    });

    startY = doc.lastAutoTable.finalY + 8;

    // Tableau détaillé
    doc.setFontSize(12);
    doc.text('Détail des temps', 14, startY);
    startY += 2;

    doc.autoTable({
      startY,
      head: [['Personne', 'Semaine', 'Jours', 'Phase', 'Commentaire']],
      body: projet.temps_consommes.map(temps => [
        `${temps.personne.prenom} ${temps.personne.nom}`,
        temps.semaine,
        parseFloat(temps.jours_consommes).toFixed(1),
        CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase || '-',
        temps.commentaire || '',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 22 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 28 },
        4: { cellWidth: 'auto' },
      },
      margin: { left: 14 },
    });

    doc.save(`rapport_${projet.nom.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  };

  // Gestion des états de chargement et d'erreur
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="flex items-center justify-center">
            <Clock className="h-6 w-6 text-gray-400 animate-spin mr-2" />
            <p className="text-gray-500">Chargement du projet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/projets" className="text-primary-600 hover:text-primary-700 inline-block">
            Retour aux projets
          </Link>
        </div>
      </div>
    );
  }

  if (!projet) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Projet non trouvé</p>
          <Link to="/projets" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
            Retour aux projets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link
            to="/projets"
            className="flex items-center text-gray-500 hover:text-gray-700 mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{projet.nom}</h1>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleStartProjectEdit}
            className="btn-secondary"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier le projet
          </button>
          <button
            onClick={handleStartDelete}
            className="btn-danger"
          >
            {canDeleteProject() ? (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </>
            ) : (
              <>
                <Archive className="h-4 w-4 mr-2" />
                Archiver
              </>
            )}
          </button>
          <button
            onClick={() => setShowTempsModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter du temps
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
            <p className="text-gray-700">{projet.description}</p>
          </div>

          {/* Planification des phases - Gantt Chart */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Planification des phases</h2>
              <button
                onClick={handleStartProjectEdit}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium flex items-center"
              >
                <Edit className="h-4 w-4 mr-1" />
                Modifier
              </button>
            </div>
            <GanttChart />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique de l'évolution du temps consommé */}
            {chartData && (
              <div className="card lg:col-span-2">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Évolution du temps consommé</h2>
                <div className="h-80">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            )}

          
              {/* Graphique par phase */}
              {phaseChartData && (
                <div className="card">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Consommé par phase vs Budget</h2>
                  <div className="h-80">
                    <Bar data={phaseChartData} options={phaseChartOptions} />
                  </div>
                </div>
              )}

              {/* Liste des personnes affectées */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Personnes affectées</h2>
                  <button
                    onClick={() => {
                      setShowAffectationModal(true);
                      setSelectedPersonnesForAffectation([]);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Affecter
                  </button>
                </div>

                {getAllPersonnesForProject().length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Aucune personne affectée à ce projet</p>
                ) : (
                  <div className="space-y-3">
                    {getAllPersonnesForProject().map((personne) => (
                      <div key={personne.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center flex-1">
                            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                              <span className="text-primary-600 font-medium text-sm">
                                {personne.prenom[0]}{personne.nom[0]}
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {personne.prenom} {personne.nom}
                              </p>
                              <p className="text-xs text-gray-500">{personne.role}</p>
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <p className="text-sm font-medium text-gray-900">
                              {personne.taux_journalier}€/j
                            </p>
                          </div>
                        </div>
                        
                        {/* Phases affectées */}
                        {personne.phasesAffectees && personne.phasesAffectees.length > 0 && (
                          <div className="space-y-2 mt-2 pl-11">
                            {projet?.rattachements_projet?.map((rattachement) => {
                              // Afficher seulement les rattachements pour cette personne
                              if (rattachement.id_personne !== personne.id || !rattachement.actif) {
                                return null;
                              }
                              
                              const categorie = CATEGORIES_TEMPS.find(cat => cat.value === rattachement.phase);
                              const colorClass = categorie ? categorie.color : 'bg-gray-100 text-gray-600';
                              
                              return (
                                <div key={rattachement.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100">
                                  <div className="flex items-center gap-2 flex-1">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                                      {categorie ? categorie.label : rattachement.phase}
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      {rattachement.role_projet === 'chef_projet' ? 'Chef de projet' :
                                       rattachement.role_projet === 'developpeur' ? 'Développeur' :
                                       rattachement.role_projet === 'testeur' ? 'Testeur' :
                                       rattachement.role_projet === 'analyste' ? 'Analyste' :
                                       'Designer'}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setAffectationToDelete(rattachement);
                                      setShowDeleteAffectationModal(true);
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded ml-2"
                                    title="Supprimer cette affectation"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
           
          </div>

          {/* Section des temps consommés */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Temps consommés par personne</h2>
              <div className="flex items-center gap-2">
                {loadingTemps && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-1 animate-spin" />
                    Chargement...
                  </div>
                )}
                {projet?.temps_consommes?.length > 0 && (
                  <>
                    <button
                      onClick={handleExportExcel}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                      Excel
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <FileText className="h-4 w-4 mr-1.5" />
                      PDF
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Résumé */}
            {Object.keys(tempsParPersonne).length > 0 && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Résumé par personne */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-800 mb-2">Jours par personne</h3>
                  <div className="space-y-1">
                    {resumeParPersonne.map((r) => (
                      <div key={r.nom} className="flex justify-between text-sm">
                        <span className="text-blue-700">{r.nom}</span>
                        <span className="font-medium text-blue-900">{r.totalJours.toFixed(1)} j</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-300 flex justify-between text-sm font-bold text-blue-900">
                    <span>Total</span>
                    <span>{totalTempsConsomme.toFixed(1)} j</span>
                  </div>
                </div>

                {/* Résumé par phase */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-purple-800 mb-2">Jours par phase</h3>
                  <div className="space-y-1">
                    {Object.entries(resumeParPhase).map(([phase, jours]) => (
                      <div key={phase} className="flex justify-between text-sm">
                        <span className="text-purple-700">{phase}</span>
                        <span className="font-medium text-purple-900">{jours.toFixed(1)} j</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-purple-300 flex justify-between text-sm font-bold text-purple-900">
                    <span>Total</span>
                    <span>{totalTempsConsomme.toFixed(1)} j</span>
                  </div>
                </div>
              </div>
            )}
            
            {Object.keys(tempsParPersonne).length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucun temps consommé enregistré pour ce projet</p>
            ) : (
              <div className="space-y-4">
                {Object.values(tempsParPersonne).map((data) => (
                  <div key={data.personne.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-primary-600 font-medium text-sm">
                            {data.personne.prenom[0]}{data.personne.nom[0]}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {data.personne.prenom} {data.personne.nom}
                          </h3>
                          <p className="text-sm text-gray-500">{data.personne.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-gray-400 mr-1" />
                            <span className="font-medium">{data.totalJours.toFixed(1)} j</span>
                          </div>
                          {data.caEstime > 0 && (
                            <div className="flex items-center">
                              <EuroIcon className="h-4 w-4 text-green-500 mr-1" />
                              <span className="font-medium text-green-600">{data.caEstime.toFixed(0)}€</span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Facturable: {data.totalFacturable.toFixed(1)} j
                        </div>
                      </div>
                    </div>
                    
                    {/* Résumé des temps par phase */}
                    {Object.keys(data.tempsParPhase).length > 0 && (
                      <div className="mt-3 mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Temps par phase :</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(data.tempsParPhase).map(([phase, temps]) => {
                            const categorie = CATEGORIES_TEMPS.find(cat => cat.value === phase);
                            const label = categorie ? categorie.label : (phase === 'non_specifie' ? 'Non spécifié' : phase);
                            const colorClass = categorie ? categorie.color : 'bg-gray-100 text-gray-600';
                            
                            return (
                              <span 
                                key={phase}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
                              >
                                {label}: {temps.toFixed(1)}j
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Tableau des temps par semaine */}
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-1 text-left font-medium text-gray-700">Semaine</th>
                            <th className="px-2 py-1 text-center font-medium text-gray-700">Jours</th>
                            <th className="px-2 py-1 text-center font-medium text-gray-700">Phase</th>
                            <th className="px-2 py-1 text-center font-medium text-gray-700">Facturable</th>
                            <th className="px-2 py-1 text-left font-medium text-gray-700">Commentaire</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.temps.map((temps) => (
                            <tr key={`${temps.id_personne}-${temps.semaine}`} className="hover:bg-gray-50">
                              <td className="px-2 py-1 font-medium text-gray-900">
                                Semaine {temps.semaine}
                              </td>
                              <td className="px-2 py-1 text-center">
                                <span className="font-medium">{temps.jours_consommes} j</span>
                              </td>
                              <td className="px-2 py-1 text-center">
                                {temps.phase && (
                                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                    CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.color || 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase}
                                  </span>
                                )}
                                {!temps.phase && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-center">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  temps.facturable 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {temps.facturable ? 'Oui' : 'Non'}
                                </span>
                              </td>
                              <td className="px-2 py-1 text-gray-500 max-w-xs truncate">
                                {temps.commentaire || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="space-y-3 text-left">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Période</p>
                  <p className="font-medium">{formatDate(projet.date_debut)} - {formatDate(projet.date_fin)}</p>
                </div>
              </div>
              <div className="flex items-center">
                <EuroIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Budget planifié</p>
                  <p className="font-medium">{projet.budget_total} jours</p>
                </div>
              </div>
              
              {/* Détails des phases */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Détail des phases</h4>
                <div className="space-y-3 text-xs">
                  {/* Phase Analyse & Design */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                      <span className="text-gray-700">Analyse & Design</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{parseFloat(projet.budget_analyse_design || 0)}j</div>
                      {projet.date_debut_analyse_design && projet.date_fin_analyse_design && (
                        <div className="text-gray-500">
                          {formatDate(projet.date_debut_analyse_design)} - {formatDate(projet.date_fin_analyse_design)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Phase Développement */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                      <span className="text-gray-700">Développement</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{parseFloat(projet.budget_build || 0)}j</div>
                      {projet.date_debut_build && projet.date_fin_build && (
                        <div className="text-gray-500">
                          {formatDate(projet.date_debut_build)} - {formatDate(projet.date_fin_build)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Phase Tests */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
                      <span className="text-gray-700">Tests</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{parseFloat(projet.budget_test || 0)}j</div>
                      {projet.date_debut_test && projet.date_fin_test && (
                        <div className="text-gray-500">
                          {formatDate(projet.date_debut_test)} - {formatDate(projet.date_fin_test)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Phase Stabilisation & Lancement */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-purple-500 rounded mr-2"></div>
                      <span className="text-gray-700">Stabilisation & Lancement</span>
                    </div>
                    <div className="text-right">
                      {(() => {
                        const stabValues = calculateStabilisationValues(projet);
                        return (
                          <>
                            <div className="font-medium">{stabValues.budget}j</div>
                            {stabValues.dateDebut && stabValues.dateFin && (
                              <div className="text-gray-500">
                                {formatDate(stabValues.dateDebut)} - {formatDate(stabValues.dateFin)}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Temps consommé</p>
                  <p className="font-medium">{totalTempsConsomme.toFixed(1)} jours</p>
                </div>
              </div>
              <div className="flex items-center">
                <EuroIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Budget restant</p>
                  <p className={`font-medium ${budgetRestant >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {budgetRestant.toFixed(1)} jours
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="h-5 w-5 mr-3 flex items-center justify-center">
                  <div className="text-gray-400 font-bold">%</div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Consommation</p>
                  <div className="flex items-center space-x-2">
                    <p className="font-medium">{pourcentageConsommation.toFixed(1)}%</p>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          pourcentageConsommation <= 80 ? 'bg-green-500' :
                          pourcentageConsommation <= 100 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(pourcentageConsommation, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Statut</p>
                  {!isEditingStatut ? (
                    <div className="flex items-center space-x-2">
                      <span className={`badge ${
                        projet.statut === 'en_cours' ? 'badge-success' :
                        projet.statut === 'planifie' ? 'badge-info' :
                        projet.statut === 'termine' ? 'badge-warning' :
                        'badge-danger'
                      }`}>
                        {STATUTS_PROJET.find(s => s.value === projet.statut)?.label || projet.statut}
                      </span>
                      <button
                        onClick={handleStartStatutEdit}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                        disabled={loadingStatut}
                      >
                        Modifier
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 mt-1">
                      <select
                        value={statutEnEdition}
                        onChange={(e) => setStatutEnEdition(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={loadingStatut}
                      >
                        {STATUTS_PROJET.map((statut) => (
                          <option key={statut.value} value={statut.value}>
                            {statut.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleStatutChange(statutEnEdition)}
                        disabled={loadingStatut || statutEnEdition === projet.statut}
                        className="flex items-center text-sm bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingStatut ? (
                          <Clock className="h-3 w-3 animate-spin" />
                        ) : (
                          <Check className="h-3 w-3" />
                        )}
                      </button>
                      <button
                        onClick={handleCancelStatutEdit}
                        disabled={loadingStatut}
                        className="flex items-center text-sm bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de saisie de temps */}
      {showTempsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowTempsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Ajouter du temps - {projet.nom}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Projet {projet.facturable ? 'facturable' : 'non facturable'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {saved && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-1" />
                      <span className="text-sm font-medium">Sauvegardé</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowTempsModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Formulaire */}
              <div className="space-y-6">
                {/* Contexte de saisie */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="form-label">Semaine *</label>
                    <input
                      type="week"
                      value={selectedWeek}
                      onChange={(e) => setSelectedWeek(e.target.value)}
                      className={`form-input ${errors.semaine ? 'border-red-500' : ''}`}
                    />
                    {errors.semaine && (
                      <p className="mt-1 text-sm text-red-600">{errors.semaine}</p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="form-label">Personnes *</label>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`form-input w-full text-left flex items-center justify-between ${errors.personnes ? 'border-red-500' : ''}`}
                    >
                      <span className="text-gray-700">
                        {selectedPersonnes.length === 0
                          ? 'Sélectionnez des personnes'
                          : `${selectedPersonnes.length} personne(s) sélectionnée(s)`
                        }
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {/* Select All option */}
                        <div 
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                          onClick={toggleSelectAll}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                            {selectedPersonnes.length === state.personnes?.data?.filter(p => p.actif).length && 
                             selectedPersonnes.length > 0 && (
                              <Check className="h-3 w-3 text-primary-600" />
                            )}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            Tout sélectionner
                          </span>
                        </div>

                        {/* Individual options */}
                        {state.personnes?.data?.filter(p => p.actif).map(personne => (
                          <div 
                            key={personne.id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            onClick={() => togglePersonne(personne.id.toString())}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                              {selectedPersonnes.includes(personne.id.toString()) && (
                                <Check className="h-3 w-3 text-primary-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm text-gray-900">
                                {personne.prenom} {personne.nom}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                ({personne.role})
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Empty state */}
                        {(!state.personnes?.data?.filter(p => p.actif) || state.personnes.data.filter(p => p.actif).length === 0) && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            Aucune personne active disponible
                          </div>
                        )}
                      </div>
                    )}

                    {errors.personnes && (
                      <p className="mt-1 text-sm text-red-600">{errors.personnes}</p>
                    )}
                    {selectedPersonnes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selectedPersonnes.map(personneId => {
                          const personne = state.personnes?.data?.find(p => p.id.toString() === personneId);
                          return personne ? (
                            <span 
                              key={personneId}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {personne.prenom} {personne.nom}
                              <button
                                type="button"
                                onClick={() => togglePersonne(personneId)}
                                className="ml-1 h-3 w-3 rounded-full hover:bg-primary-200 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Temps consommés */}
                {selectedPersonnes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Sélectionnez des personnes pour saisir leurs temps</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedPersonnes.map((personneId) => {
                      const personne = state.personnes?.data?.find(p => p.id === parseInt(personneId));
                      const tempsData = tempsDataByPersonne[personneId] || {};
                      
                      return (
                        <div key={personneId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center mb-4">
                            <User className="h-5 w-5 text-primary-600 mr-2" />
                            <h4 className="text-lg font-medium text-gray-900">
                              {personne?.prenom} {personne?.nom} ({personne?.role})
                            </h4>
                            <span className="ml-auto text-sm text-gray-600">
                              Taux: {personne?.taux_journalier}€/jour
                            </span>
                          </div>

                          {/* Affichage des temps existants pour cette personne et cette semaine */}
                          {existingTempsbyPersonne[personneId] && existingTempsbyPersonne[personneId].length > 0 && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <h5 className="text-sm font-medium text-blue-800 mb-2">
                                Temps déjà saisis pour cette semaine :
                              </h5>
                              <div className="space-y-2">
                                {existingTempsbyPersonne[personneId].map((temps, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-blue-700 font-medium">
                                        {temps.projet?.nom}
                                      </span>
                                      <span className="text-blue-600">
                                        - {temps.jours_consommes} jour{temps.jours_consommes > 1 ? 's' : ''}
                                      </span>
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                        temps.facturable 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {temps.facturable ? 'Facturable' : 'Non fact.'}
                                      </span>
                                    </div>
                                    {temps.commentaire && (
                                      <span className="text-blue-600 text-xs" title={temps.commentaire}>
                                        💬
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="mt-2 pt-2 border-t border-blue-200">
                                <span className="text-sm font-medium text-blue-800">
                                  Total semaine : {existingTempsbyPersonne[personneId].reduce((total, temps) => total + parseFloat(temps.jours_consommes), 0).toFixed(1)} jours
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div>
                              <label className="form-label">Jours consommés *</label>
                              <input
                                type="number"
                                value={tempsData.jours_consommes || ''}
                                onChange={(e) => handleTempsChange(personneId, 'jours_consommes', e.target.value)}
                                step="0.5"
                                min="0"
                                max="5"
                                className={`form-input ${errors[`${personneId}_jours_consommes`] ? 'border-red-500' : ''}`}
                                placeholder="0.0"
                              />
                              {errors[`${personneId}_jours_consommes`] && (
                                <p className="mt-1 text-sm text-red-600">{errors[`${personneId}_jours_consommes`]}</p>
                              )}
                            </div>

                            <div>
                              <label className="form-label">Phase *</label>
                              <select
                                value={tempsData.phase || 'build'}
                                onChange={(e) => handleTempsChange(personneId, 'phase', e.target.value)}
                                className="form-input"
                              >
                                {CATEGORIES_TEMPS.map((categorie) => (
                                  <option key={categorie.value} value={categorie.value}>
                                    {categorie.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="form-label">Type de facturation</label>
                              <select
                                value={tempsData.facturable}
                                onChange={(e) => handleTempsChange(personneId, 'facturable', e.target.value === 'true')}
                                className="form-input"
                              >
                                {TYPES_FACTURATION.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-sm text-gray-500">
                                {tempsData.facturable 
                                  ? 'Ce temps sera facturé au client'
                                  : 'Temps interne non facturable'
                                }
                              </p>
                            </div>

                            <div>
                              <label className="form-label">Montant estimé</label>
                              <div className="mt-1 p-3 bg-white border border-gray-300 rounded-md">
                                <span className="text-sm font-medium text-gray-900">
                                  {personne && tempsData.jours_consommes && tempsData.facturable
                                    ? `${(parseFloat(tempsData.jours_consommes) * personne.taux_journalier).toFixed(2)}€`
                                    : '0€'
                                  }
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="form-label">Commentaire *</label>
                            <textarea
                              value={tempsData.commentaire || ''}
                              onChange={(e) => handleTempsChange(personneId, 'commentaire', e.target.value)}
                              className={`form-input ${errors[`${personneId}_commentaire`] ? 'border-red-500' : ''}`}
                              rows="3"
                              placeholder="Décrivez le travail de la semaine..."
                            />
                            {errors[`${personneId}_commentaire`] && (
                              <p className="mt-1 text-sm text-red-600">{errors[`${personneId}_commentaire`]}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowTempsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleSaveTemps}
                  disabled={selectedPersonnes.length === 0 || !selectedWeek}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition du projet */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Modifier le projet</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Modifiez les informations générales et la planification des phases
                  </p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Erreur générale */}
              {editErrors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{editErrors.general}</p>
                </div>
              )}

              {/* Formulaire */}
              <form onSubmit={(e) => { e.preventDefault(); handleSaveProject(); }} className="space-y-6">
                {/* Informations générales */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="edit-nom" className="form-label">
                        Nom du projet *
                      </label>
                      <input
                        type="text"
                        id="edit-nom"
                        name="nom"
                        value={editFormData.nom}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.nom ? 'border-red-500' : ''}`}
                        placeholder="Ex: Refonte Site E-commerce"
                      />
                      {editErrors.nom && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.nom}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="edit-description" className="form-label">
                        Description *
                      </label>
                      <textarea
                        id="edit-description"
                        name="description"
                        rows={3}
                        value={editFormData.description}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.description ? 'border-red-500' : ''}`}
                        placeholder="Décrivez les objectifs et le scope du projet..."
                      />
                      {editErrors.description && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.description}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-date_debut" className="form-label">
                        Date de début *
                      </label>
                      <input
                        type="date"
                        id="edit-date_debut"
                        name="date_debut"
                        value={editFormData.date_debut}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.date_debut ? 'border-red-500' : ''}`}
                      />
                      {editErrors.date_debut && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.date_debut}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-date_fin" className="form-label">
                        Date de fin *
                      </label>
                      <input
                        type="date"
                        id="edit-date_fin"
                        name="date_fin"
                        value={editFormData.date_fin}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.date_fin ? 'border-red-500' : ''}`}
                      />
                      {editErrors.date_fin && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.date_fin}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Budgets par phase */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Budgets par phase</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="edit-budget_analyse_design" className="form-label">
                        Analyse & Design (jours)
                      </label>
                      <input
                        type="number"
                        id="edit-budget_analyse_design"
                        name="budget_analyse_design"
                        value={editFormData.budget_analyse_design}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.budget_analyse_design ? 'border-red-500' : ''}`}
                        placeholder="15"
                        min="0"
                        step="0.5"
                      />
                      {editErrors.budget_analyse_design && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.budget_analyse_design}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-budget_build" className="form-label">
                        Développement (jours)
                      </label>
                      <input
                        type="number"
                        id="edit-budget_build"
                        name="budget_build"
                        value={editFormData.budget_build}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.budget_build ? 'border-red-500' : ''}`}
                        placeholder="50"
                        min="0"
                        step="0.5"
                      />
                      {editErrors.budget_build && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.budget_build}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-budget_test" className="form-label">
                        Tests (jours)
                      </label>
                      <input
                        type="number"
                        id="edit-budget_test"
                        name="budget_test"
                        value={editFormData.budget_test}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.budget_test ? 'border-red-500' : ''}`}
                        placeholder="20"
                        min="0"
                        step="0.5"
                      />
                      {editErrors.budget_test && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.budget_test}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="edit-budget_stabilisation_lancement" className="form-label">
                        Stabilisation & Lancement (jours)
                      </label>
                      <input
                        type="number"
                        id="edit-budget_stabilisation_lancement"
                        name="budget_stabilisation_lancement"
                        value={editFormData.budget_stabilisation_lancement}
                        onChange={handleEditFormChange}
                        className={`form-input ${editErrors.budget_stabilisation_lancement ? 'border-red-500' : ''}`}
                        placeholder="20"
                        min="0"
                        step="0.5"
                      />
                      {editErrors.budget_stabilisation_lancement && (
                        <p className="mt-1 text-sm text-red-600">{editErrors.budget_stabilisation_lancement}</p>
                      )}
                    </div>
                  </div>

                  {/* Total des budgets */}
                  <div className="mt-4 p-3 bg-primary-50 rounded-lg border border-primary-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary-900">
                        Total des budgets
                      </span>
                      <span className="text-lg font-bold text-primary-900">
                        {((parseFloat(editFormData.budget_analyse_design) || 0) + 
                          (parseFloat(editFormData.budget_build) || 0) + 
                          (parseFloat(editFormData.budget_test) || 0) +
                          (parseFloat(editFormData.budget_stabilisation_lancement) || 0)).toFixed(1)} jours
                      </span>
                    </div>
                    {editErrors.budget_total && (
                      <p className="mt-1 text-sm text-red-600">{editErrors.budget_total}</p>
                    )}
                  </div>
                </div>

                {/* Planification des phases */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Planification des phases</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Définissez les dates de début et de fin pour chaque phase. Ces dates permettront de répartir automatiquement les budgets par semaine.
                  </p>
                  
                  {/* Phase Analyse & Design */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">Phase Analyse & Design</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="edit-date_debut_analyse_design" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de début
                        </label>
                        <input
                          type="date"
                          id="edit-date_debut_analyse_design"
                          name="date_debut_analyse_design"
                          value={editFormData.date_debut_analyse_design}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_debut_analyse_design ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_debut_analyse_design && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_debut_analyse_design}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="edit-date_fin_analyse_design" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          id="edit-date_fin_analyse_design"
                          name="date_fin_analyse_design"
                          value={editFormData.date_fin_analyse_design}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_fin_analyse_design ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_fin_analyse_design && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_fin_analyse_design}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phase Développement */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-green-900 mb-3">Phase Développement</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="edit-date_debut_build" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de début
                        </label>
                        <input
                          type="date"
                          id="edit-date_debut_build"
                          name="date_debut_build"
                          value={editFormData.date_debut_build}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_debut_build ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_debut_build && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_debut_build}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="edit-date_fin_build" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          id="edit-date_fin_build"
                          name="date_fin_build"
                          value={editFormData.date_fin_build}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_fin_build ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_fin_build && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_fin_build}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phase Tests */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-orange-900 mb-3">Phase Tests</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="edit-date_debut_test" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de début
                        </label>
                        <input
                          type="date"
                          id="edit-date_debut_test"
                          name="date_debut_test"
                          value={editFormData.date_debut_test}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_debut_test ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_debut_test && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_debut_test}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="edit-date_fin_test" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          id="edit-date_fin_test"
                          name="date_fin_test"
                          value={editFormData.date_fin_test}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_fin_test ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_fin_test && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_fin_test}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phase Stabilisation & Lancement */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-purple-900 mb-3">Phase Stabilisation & Lancement</h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="edit-date_debut_stabilisation_lancement" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de début
                        </label>
                        <input
                          type="date"
                          id="edit-date_debut_stabilisation_lancement"
                          name="date_debut_stabilisation_lancement"
                          value={editFormData.date_debut_stabilisation_lancement}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_debut_stabilisation_lancement ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_debut_stabilisation_lancement && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_debut_stabilisation_lancement}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="edit-date_fin_stabilisation_lancement" className="block text-sm font-medium text-gray-700 mb-1">
                          Date de fin
                        </label>
                        <input
                          type="date"
                          id="edit-date_fin_stabilisation_lancement"
                          name="date_fin_stabilisation_lancement"
                          value={editFormData.date_fin_stabilisation_lancement}
                          onChange={handleEditFormChange}
                          className={`form-input ${editErrors.date_fin_stabilisation_lancement ? 'border-red-500' : ''}`}
                        />
                        {editErrors.date_fin_stabilisation_lancement && (
                          <p className="mt-1 text-sm text-red-600">{editErrors.date_fin_stabilisation_lancement}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit"
                    disabled={savingProject}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingProject ? (
                      <>
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression/archivage */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {deleteAction === 'delete' ? (
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mr-4">
                      <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mr-4">
                      <Archive className="h-6 w-6 text-yellow-600" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {deleteAction === 'delete' ? 'Supprimer le projet' : 'Archiver le projet'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {projet?.nom}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Message de confirmation */}
              <div className="mb-6">
                {deleteAction === 'delete' ? (
                  <div className="space-y-3">
                    <p className="text-gray-700">
                      Êtes-vous sûr de vouloir <strong>supprimer définitivement</strong> ce projet ?
                    </p>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">
                        <strong>Attention :</strong> Cette action est irréversible. Le projet et toutes ses données associées 
                        (temps planifiés, rattachements, phases) seront définitivement supprimés.
                      </p>
                    </div>
                    <p className="text-sm text-gray-600">
                      Aucun temps consommé n'a été saisi sur ce projet, il peut donc être supprimé complètement.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-gray-700">
                      Ce projet contient du temps consommé et sera <strong>archivé</strong> au lieu d'être supprimé.
                    </p>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        Le projet sera marqué comme archivé et son statut passera à "Terminé". 
                        Il ne sera plus visible dans la liste principale mais les données seront conservées.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingProject}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteProject}
                  disabled={deletingProject}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                    deleteAction === 'delete' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  {deletingProject ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin inline" />
                      {deleteAction === 'delete' ? 'Suppression...' : 'Archivage...'}
                    </>
                  ) : (
                    <>
                      {deleteAction === 'delete' ? (
                        <>
                          <Trash2 className="h-4 w-4 mr-2 inline" />
                          Supprimer définitivement
                        </>
                      ) : (
                        <>
                          <Archive className="h-4 w-4 mr-2 inline" />
                          Archiver le projet
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression d'affectation */}
      {showDeleteAffectationModal && affectationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDeleteAffectationModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mr-4">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Supprimer l'affectation
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {affectationToDelete?.personne?.prenom} {affectationToDelete?.personne?.nom}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteAffectationModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Message de confirmation */}
              <div className="mb-6">
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Êtes-vous sûr de vouloir <strong>supprimer cette affectation</strong> ?
                  </p>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">
                      <strong>Phase :</strong> {
                        CATEGORIES_TEMPS.find(c => c.value === affectationToDelete?.phase)?.label || affectationToDelete?.phase
                      }
                      <br />
                      <strong>Rôle :</strong> {
                        affectationToDelete?.role_projet === 'chef_projet' ? 'Chef de projet' :
                        affectationToDelete?.role_projet === 'developpeur' ? 'Développeur' :
                        affectationToDelete?.role_projet === 'testeur' ? 'Testeur' :
                        affectationToDelete?.role_projet === 'analyste' ? 'Analyste' :
                        'Designer'
                      }
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    Cette action ne supprimera pas les temps consommés enregistrés pour cette personne sur ce projet.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteAffectationModal(false)}
                  disabled={deletingAffectation}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteAffectation}
                  disabled={deletingAffectation}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {deletingAffectation ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'affectation de personnes à une phase */}
      {showAffectationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAffectationModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Affecter une personne à une phase</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Sélectionnez une phase et une ou plusieurs personnes
                  </p>
                </div>
                <button
                  onClick={() => setShowAffectationModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Formulaire */}
              <div className="space-y-6">
                {/* Sélection de la phase */}
                <div>
                  <label className="form-label">Phase *</label>
                  <select
                    value={selectedPhaseForAffectation}
                    onChange={(e) => setSelectedPhaseForAffectation(e.target.value)}
                    className="form-input"
                  >
                    {CATEGORIES_TEMPS.map((categorie) => (
                      <option key={categorie.value} value={categorie.value}>
                        {categorie.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sélection du rôle dans le projet */}
                <div>
                  <label className="form-label">Rôle dans le projet *</label>
                  <select
                    value={selectedRoleProjet}
                    onChange={(e) => setSelectedRoleProjet(e.target.value)}
                    className="form-input"
                  >
                    <option value="chef_projet">Chef de projet</option>
                    <option value="developpeur">Développeur</option>
                    <option value="testeur">Testeur</option>
                    <option value="analyste">Analyste</option>
                    <option value="designer">Designer</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Le rôle définit la fonction de la personne dans cette phase du projet
                  </p>
                </div>

                {/* Sélection des personnes */}
                <div>
                  <label className="form-label mb-3">Personnes à affecter *</label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {state.personnes?.data?.filter(p => p.actif).map(personne => (
                      <div 
                        key={personne.id}
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedPersonnesForAffectation(prev => {
                            const isSelected = prev.includes(personne.id.toString());
                            return isSelected 
                              ? prev.filter(id => id !== personne.id.toString())
                              : [...prev, personne.id.toString()];
                          });
                        }}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                          {selectedPersonnesForAffectation.includes(personne.id.toString()) && (
                            <Check className="h-3 w-3 text-primary-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {personne.prenom} {personne.nom}
                          </p>
                          <p className="text-xs text-gray-500">{personne.role}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{personne.taux_journalier}€/j</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(!state.personnes?.data?.filter(p => p.actif) || state.personnes.data.filter(p => p.actif).length === 0) && (
                    <div className="text-center py-4 text-gray-500">
                      Aucune personne active disponible
                    </div>
                  )}
                </div>

                {/* Message d'information */}
                {selectedPersonnesForAffectation.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>{selectedPersonnesForAffectation.length} personne(s)</strong> sera/seront affectée(s) en tant que 
                      <strong> {selectedRoleProjet === 'chef_projet' ? 'Chef de projet' : selectedRoleProjet === 'developpeur' ? 'Développeur' : selectedRoleProjet === 'testeur' ? 'Testeur' : selectedRoleProjet === 'analyste' ? 'Analyste' : 'Designer'}</strong> à la phase 
                      <strong> {CATEGORIES_TEMPS.find(c => c.value === selectedPhaseForAffectation)?.label}</strong>.
                      <br />
                      Les temps saisis pour ces personnes seront enregistrés sous cette phase avec ce rôle.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAffectationModal(false);
                    setSelectedPersonnesForAffectation([]);
                  }}
                  disabled={savingAffectation}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleSaveAffectation}
                  disabled={selectedPersonnesForAffectation.length === 0 || savingAffectation}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingAffectation ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Affectation...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Confirmer l'affectation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjetDetail; 